import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";

import SubmitLeaveModal from "./CreateLeaveRequestModal";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { leaveApi } from "../../../api/leaveApi";
import { useCanApproveLeave } from "../../../api/queries/leaveQueries";
import LeaveStatusBadge from "./review/LeaveStatusBadge";
import {
  LeaveChangeSummary,
  LeaveChangeTag,
} from "./review/LeaveChangeDetails";
import { isAwaitingDecision } from "./review/leaveFormat";
import { useLeaveReviewDialogs } from "./review/LeaveReviewDialogs";
import "../leave.css";
import { useCanModify } from "../../../hooks/usePermissions";

// One row; Approve/Reject/Cancel only when the server says this user may act on it.
const LeaveRow = ({ leave, onAction, blocked }) => {
  const isOpen = leave.status === "pending" || leave.status === "approved";
  const { data: canManage = false, isLoading: loadingPermission } =
    useCanApproveLeave(leave.id, { enabled: isOpen });

  return (
    <tr>
      <td className="fw-bold">{leave.leaveType?.name || "N/A"}</td>
      <td>{leave.reason || "N/A"}</td>
      <td>{leave.startDate || "N/A"}</td>
      <td>{leave.endDate || "N/A"}</td>
      <td>{leave.totalDays || "0"}</td>
      <td>{leave.daysFromCarryOver ?? "0"}</td>
      <td>{leave.daysFromAllocated ?? "0"}</td>

      <td className="text-center">
        <div className="d-flex flex-wrap justify-content-center gap-1">
          <LeaveStatusBadge status={leave.status} />
          <LeaveChangeTag request={leave} />
        </div>
        <LeaveChangeSummary request={leave} />
      </td>

      <td className="text-center text-nowrap">
        {isOpen && loadingPermission ? (
          <span
            className="spinner-border spinner-border-sm text-secondary"
            role="status"
            aria-label="Checking permission"
          />
        ) : (
          canManage &&
          !blocked && (
            <>
              {isAwaitingDecision(leave) && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success fw-bold m-1 fs-075 rounded-6"
                    onClick={() => onAction("approve", leave)}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger fw-bold m-1 fs-075 rounded-6"
                    onClick={() => onAction("reject", leave)}
                  >
                    Reject
                  </button>
                </>
              )}
              {!leave.pendingChange && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning fw-bold m-1 fs-075 rounded-6"
                  onClick={() => onAction("cancel", leave)}
                >
                  Cancel
                </button>
              )}
            </>
          )
        )}
      </td>
    </tr>
  );
};

const EmployeeLeaveListView = (props) => {
  const { id: propId, onRefresh } = props;
  const canModify = useCanModify("leave"); // approve / reject / cancel
  const queryClient = useQueryClient();
  const routeParams = useParams();

  const id = propId || routeParams.id;

  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);

  const [leaveTypeFilter, setLeaveTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const { data: leaveList = [] } = useQuery({
    queryKey: ["leaveList", id],
    queryFn: async () => {
      const response = await leaveApi.getEmployeeLeaveRequests(id);
      return Array.isArray(response) ? response : response.data || [];
    },
    enabled: !!id,
  });

  const uniqueLeaveTypes = useMemo(() => {
    const typesMap = new Map();
    leaveList.forEach((leave) => {
      if (leave.leaveType && leave.leaveType.id) {
        typesMap.set(leave.leaveType.id, leave.leaveType.name);
      }
    });
    return Array.from(typesMap, ([id, name]) => ({ id, name }));
  }, [leaveList]);

  const uniqueStatuses = useMemo(() => {
    const statusesSet = new Set();
    leaveList.forEach((leave) => {
      if (leave.status) {
        statusesSet.add(leave.status);
      }
    });
    return Array.from(statusesSet);
  }, [leaveList]);

  const filteredLeaveList = useMemo(() => {
    return leaveList.filter((leave) => {
      const matchesLeaveType =
        leaveTypeFilter === "all" || leave.leaveType?.id === leaveTypeFilter;
      const matchesStatus =
        statusFilter === "all" || leave.status === statusFilter;
      return matchesLeaveType && matchesStatus;
    });
  }, [leaveList, leaveTypeFilter, statusFilter]);

  // Approve / reject (with reason) / cancel open a dialog; each refreshes lists and balances.
  const review = useLeaveReviewDialogs();
  const wasOpen = React.useRef(false);
  React.useEffect(() => {
    // Let the parent page refresh its own data after a dialog closes
    if (wasOpen.current && !review.isOpen) onRefresh?.();
    wasOpen.current = review.isOpen;
  }, [review.isOpen, onRefresh]);

  return (
    <div className="container-fluid px-0">
      <div className="bg-white p-3 rounded-3 shadow-sm border mb-4 position-relative">
        <div className="d-flex justify-content-between align-items-center px-3 py-2 rounded-2 mb-4 section-bar">
          <div className="d-flex align-items-center">
            <i className="bi bi-person-vcard-fill text-white me-2 fs-090"></i>
            <h6 className="m-0 text-white text-uppercase fs-075">
              Applied Leave
            </h6>
          </div>

          <button
            type="button"
            className="d-flex align-items-center bg-transparent border-0 p-0 text-start cursor-pointer transition-opacity"
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            onClick={() => setIsSubmitModalOpen(true)}
          >
            <div className="d-flex align-items-center">
              <i className="bi bi-plus text-white me-2 fs-090"></i>
              <h6 className="m-0 text-white text-uppercase fs-075">
                Create Leave Request
              </h6>
            </div>
          </button>
        </div>

        {/* Filter Controls */}
        <div className="row g-3 mb-3 px-2">
          <div className="col-md-4">
            <label className="form-label small fw-bold">
              Filter by Leave Type
            </label>
            <select
              className="form-select form-select-sm"
              value={leaveTypeFilter}
              onChange={(e) => setLeaveTypeFilter(e.target.value)}
            >
              <option value="all">All Leave Types</option>
              {uniqueLeaveTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label small fw-bold">Filter by Status</label>
            <select
              className="form-select form-select-sm text-uppercase"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              {uniqueStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row g-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle shadow-sm bg-white">
              <thead className="table-light">
                <tr>
                  <th>Leave Type</th>
                  <th>Reason</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Total Days</th>
                  <th>Carry Over Days</th>
                  <th>Allocated Days</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeaveList && filteredLeaveList.length > 0 ? (
                  filteredLeaveList.map((leave) => (
                    <LeaveRow
                      key={leave.id}
                      leave={leave}
                      onAction={review.open}
                      blocked={review.isForbidden(leave.id) || !canModify}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center text-muted py-4">
                      No leave requests found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {review.dialog}

      {isSubmitModalOpen && (
        <SubmitLeaveModal
          employeeId={id}
          onClose={() => {
            setIsSubmitModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ["leaveList", id] });
            queryClient.invalidateQueries({
              queryKey: ["employeeLeaveBalances", id],
            });
            if (onRefresh) onRefresh();
          }}
        />
      )}
    </div>
  );
};

export default EmployeeLeaveListView;
